import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { toast } from 'sonner';
import { Plus, Trash2, CalendarRange, Euro, TrendingUp, Save, Eye, Pencil, X, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMMDD = (v) => (v && v.length === 5 ? `${v.slice(3)}/${v.slice(0, 2)}` : v);
const rangeKey = (r) => `${r.from}-${r.to}`;
const rangeLabel = (r) => `${r.from} - ${r.to}`;
const eur = (v) => `${(Math.round(v * 100) / 100).toFixed(2)} EUR`;
const zeroTiers = () => Array.from({ length: 10 }, (_, i) => ({ from_pct: i * 10, pct: 0 }));

export default function SeasonPricing({ isAdmin }) {
  const [seasons, setSeasons] = useState([]);
  const [models, setModels] = useState([]);
  const [dayRanges, setDayRanges] = useState([]);
  const [basePrices, setBasePrices] = useState({});
  const [modelTiers, setModelTiers] = useState({});
  const [seasonPcts, setSeasonPcts] = useState({});
  const [newSeason, setNewSeason] = useState({ nombre: '', date_from: '', date_to: '', pct_increment: '' });
  const [editSeason, setEditSeason] = useState(null);
  const [newRange, setNewRange] = useState({ from: '', to: '' });
  const [previewModelId, setPreviewModelId] = useState('');
  const [priceModelId, setPriceModelId] = useState('');

  const load = useCallback(async () => {
    try {
      const [sRes, mRes, cRes] = await Promise.all([
        axios.get(`${API}/catalog/seasons`),
        axios.get(`${API}/catalog/models`),
        axios.get(`${API}/catalog/pricing-config`),
      ]);
      setSeasons(sRes.data || []);
      const pcts = {};
      (sRes.data || []).forEach(s => { pcts[s.id] = s.pct_increment ?? 0; });
      setSeasonPcts(pcts);
      setModels(mRes.data || []);
      const bp = {}, mt = {};
      (mRes.data || []).forEach(m => {
        bp[m.id] = { ...(m.base_day_prices || {}) };
        mt[m.id] = m.occupancy_tiers?.length ? m.occupancy_tiers : zeroTiers();
      });
      setBasePrices(bp);
      setModelTiers(mt);
      setDayRanges(cRes.data?.day_ranges || []);
      setPreviewModelId(prev => prev || ((mRes.data || [])[0]?.id ?? ''));
      setPriceModelId(prev => prev || ((mRes.data || [])[0]?.id ?? ''));
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ===== Seasons =====
  const addSeason = async (e) => {
    e.preventDefault();
    if (!newSeason.nombre.trim() || !newSeason.date_from || !newSeason.date_to) {
      toast.error('Nombre y fechas son obligatorios');
      return;
    }
    try {
      await axios.post(`${API}/catalog/seasons`, {
        nombre: newSeason.nombre.trim(),
        date_from: newSeason.date_from.slice(5),
        date_to: newSeason.date_to.slice(5),
        pct_increment: parseFloat(newSeason.pct_increment) || 0,
      });
      toast.success(`Temporada "${newSeason.nombre.trim()}" creada`);
      setNewSeason({ nombre: '', date_from: '', date_to: '', pct_increment: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear temporada');
    }
  };

  const deleteSeason = async (s) => {
    if (!window.confirm(`¿Eliminar la temporada "${s.nombre}"?`)) return;
    try {
      await axios.delete(`${API}/catalog/seasons/${s.id}`);
      toast.success('Temporada eliminada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const startEditSeason = (s) => setEditSeason({
    id: s.id, nombre: s.nombre,
    date_from: `2026-${s.date_from}`, date_to: `2026-${s.date_to}`,
  });

  const saveEditSeason = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/catalog/seasons/${editSeason.id}`, {
        nombre: editSeason.nombre.trim(),
        date_from: editSeason.date_from.slice(5),
        date_to: editSeason.date_to.slice(5),
      });
      toast.success('Temporada actualizada');
      setEditSeason(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar temporada');
    }
  };

  const savePcts = async () => {
    try {
      await Promise.all(seasons.map(s =>
        axios.put(`${API}/catalog/seasons/${s.id}`, { pct_increment: parseFloat(seasonPcts[s.id]) || 0 })
      ));
      toast.success('Incrementos por temporada guardados');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  // ===== Day ranges =====
  const saveRanges = async (ranges) => {
    try {
      await axios.put(`${API}/catalog/pricing-config`, { day_ranges: ranges });
      toast.success('Rangos de días guardados');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar rangos');
    }
  };

  const addRange = (e) => {
    e.preventDefault();
    const f = parseInt(newRange.from), to = parseInt(newRange.to);
    if (!f || !to || f < 1 || to < f) { toast.error('Rango inválido (desde ≥ 1 y hasta ≥ desde)'); return; }
    if (dayRanges.some(r => r.from === f && r.to === to)) { toast.error('Ese rango ya existe'); return; }
    setNewRange({ from: '', to: '' });
    saveRanges([...dayRanges, { from: f, to: to }]);
  };

  const deleteRange = (r) => {
    if (!window.confirm(`¿Eliminar el rango ${rangeLabel(r)}? Los precios de ese rango dejarán de usarse.`)) return;
    saveRanges(dayRanges.filter(x => !(x.from === r.from && x.to === r.to)));
  };

  // ===== Base prices per model =====
  const setPrice = (modelId, key, value) => {
    setBasePrices(prev => ({ ...prev, [modelId]: { ...(prev[modelId] || {}), [key]: value } }));
  };

  const savePrices = async (model) => {
    const raw = basePrices[model.id] || {};
    const clean = {};
    dayRanges.forEach(r => {
      const v = raw[rangeKey(r)];
      if (v !== '' && v != null && !isNaN(parseFloat(v))) clean[rangeKey(r)] = parseFloat(v);
    });
    try {
      await axios.put(`${API}/catalog/models/${model.id}`, { base_day_prices: clean });
      toast.success(`Precios base de ${model.nombre_modelo} guardados`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar precios');
    }
  };

  // ===== Occupancy tiers per model =====
  const saveModelTiers = async (model) => {
    const tiers = (modelTiers[model.id] || zeroTiers()).map(tr => ({ from_pct: tr.from_pct, pct: parseFloat(tr.pct) || 0 }));
    try {
      await axios.put(`${API}/catalog/models/${model.id}`, { occupancy_tiers: tiers });
      toast.success(`Ocupación de ${model.nombre_modelo} guardada`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  // ===== Season activation per model =====
  const toggleSeasonForModel = async (model, seasonId) => {
    const current = model.disabled_seasons || [];
    const next = current.includes(seasonId) ? current.filter(x => x !== seasonId) : [...current, seasonId];
    try {
      await axios.put(`${API}/catalog/models/${model.id}`, { disabled_seasons: next });
      setModels(prev => prev.map(m => m.id === model.id ? { ...m, disabled_seasons: next } : m));
      const s = seasons.find(x => x.id === seasonId);
      toast.success(next.includes(seasonId)
        ? `Temporada ${s?.nombre} desactivada para ${model.nombre_modelo} (se aplica precio base)`
        : `Temporada ${s?.nombre} activada para ${model.nombre_modelo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  // ===== Export cuadrante (una pestaña por modelo) =====
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSeason, setExportSeason] = useState('all');
  const [exportModels, setExportModels] = useState(new Set());

  const modelsWithPrices = models.filter(m => Object.keys(basePrices[m.id] || {}).some(k => basePrices[m.id][k] !== '' && basePrices[m.id][k] != null));

  const openExport = () => {
    if (!modelsWithPrices.length) { toast.error('Ningún modelo tiene precios base configurados'); return; }
    setExportSeason('all');
    setExportModels(new Set(modelsWithPrices.map(m => m.id)));
    setExportOpen(true);
  };

  const exportCuadrante = async () => {
    const selected = modelsWithPrices.filter(m => exportModels.has(m.id));
    if (!selected.length) { toast.error('Selecciona al menos un modelo'); return; }
    const wb = new ExcelJS.Workbook();
    const center = { vertical: 'middle', horizontal: 'center' };
    const usedNames = new Set();

    selected.forEach((m, idx) => {
      let name = (m.nombre_modelo || `Modelo ${idx + 1}`).replace(/[[\]:*?/\\]/g, '').slice(0, 28);
      while (usedNames.has(name)) name = `${name.slice(0, 25)}_${idx}`;
      usedNames.add(name);
      const ws = wb.addWorksheet(name);
      const cols = 2 + dayRanges.length;

      const nameRow = ws.addRow([m.nombre_modelo]);
      nameRow.font = { bold: true, size: 13 };
      ws.mergeCells(nameRow.number, 1, nameRow.number, cols);

      const hdrRow = ws.addRow(['Temporada', 'Incremento %', ...dayRanges.map(rangeLabel)]);
      hdrRow.font = { bold: true };

      const disabled = new Set(m.disabled_seasons || []);
      const seasonsToExport = orderedSeasons.filter(s =>
        !disabled.has(s.id) && (exportSeason === 'all' || s.id === exportSeason));
      if (seasonsToExport.length === 0) {
        ws.addRow(['Sin temporadas activas para este modelo']);
      }
      seasonsToExport.forEach(s => {
        const pct = parseFloat(seasonPcts[s.id]) || 0;
        ws.addRow([s.nombre, `+${pct}%`, ...dayRanges.map(r => {
          const base = parseFloat(basePrices[m.id]?.[rangeKey(r)]);
          return isNaN(base) ? '' : Math.round(base * (1 + pct / 100) * 100) / 100;
        })]);
      });

      ws.columns.forEach(col => { col.width = 14; });
      ws.eachRow(row => row.eachCell({ includeEmpty: false }, cell => { cell.alignment = center; }));
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `lokki_tarifas_modelos_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
    setExportOpen(false);
    toast.success(`Tarifas exportadas: ${selected.length} modelo(s), una pestaña por modelo`);
  };

  // ===== Preview =====
  const previewModel = useMemo(() => models.find(m => m.id === previewModelId), [models, previewModelId]);
  const priceModel = useMemo(() => models.find(m => m.id === priceModelId), [models, priceModelId]);
  const orderedSeasons = useMemo(() => [...seasons].sort((a, b) => (a.pct_increment ?? 0) - (b.pct_increment ?? 0)), [seasons]);
  const previewTiers = (previewModel && modelTiers[previewModel.id]) || zeroTiers();

  return (
    <div className="space-y-6" data-testid="season-pricing">
      {/* Seasons + % increments (global) */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarRange size={18} className="text-orange-500" />
            Temporadas e incremento del precio en % (común para todos los modelos)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAdmin && (
            <form onSubmit={addSeason} className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Nombre</Label>
                <Input value={newSeason.nombre} onChange={(e) => setNewSeason({ ...newSeason, nombre: e.target.value })}
                  placeholder="Ej: Alta" className="w-36" data-testid="new-season-name" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Desde (día/mes)</Label>
                <Input type="date" value={newSeason.date_from} onChange={(e) => setNewSeason({ ...newSeason, date_from: e.target.value })}
                  className="w-40" data-testid="new-season-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta (día/mes)</Label>
                <Input type="date" value={newSeason.date_to} onChange={(e) => setNewSeason({ ...newSeason, date_to: e.target.value })}
                  className="w-40" data-testid="new-season-to" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">% incremento</Label>
                <Input type="number" step="0.5" min="0" value={newSeason.pct_increment}
                  onChange={(e) => setNewSeason({ ...newSeason, pct_increment: e.target.value })}
                  placeholder="0" className="w-24" data-testid="new-season-pct" />
              </div>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="add-season-btn">
                <Plus size={16} className="mr-1" />Añadir
              </Button>
            </form>
          )}
          <p className="text-xs text-slate-400">
            Los precios base corresponden a la temporada con 0% (ej: Baja). Las fechas se repiten cada año.
          </p>
          {seasons.length === 0 ? (
            <p className="text-sm text-slate-400">No hay temporadas definidas.</p>
          ) : (
            <div className="flex flex-wrap items-end gap-3" data-testid="seasons-list">
              {orderedSeasons.map(s => (
                editSeason?.id === s.id ? (
                  <form key={s.id} onSubmit={saveEditSeason} className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 flex flex-wrap items-end gap-2" data-testid={`season-edit-${s.id}`}>
                    <div className="space-y-1">
                      <Label className="text-xs">Nombre</Label>
                      <Input value={editSeason.nombre} onChange={(e) => setEditSeason({ ...editSeason, nombre: e.target.value })}
                        className="h-8 w-28" data-testid="edit-season-name" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Desde</Label>
                      <Input type="date" value={editSeason.date_from} onChange={(e) => setEditSeason({ ...editSeason, date_from: e.target.value })}
                        className="h-8 w-36" data-testid="edit-season-from" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Hasta</Label>
                      <Input type="date" value={editSeason.date_to} onChange={(e) => setEditSeason({ ...editSeason, date_to: e.target.value })}
                        className="h-8 w-36" data-testid="edit-season-to" />
                    </div>
                    <Button type="submit" size="sm" className="h-8 bg-orange-500 hover:bg-orange-600" data-testid="save-season-edit">
                      <Save size={13} />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setEditSeason(null)}>
                      <X size={13} />
                    </Button>
                  </form>
                ) : (
                  <div key={s.id} className="bg-slate-50 rounded-lg px-3 py-2 space-y-1" data-testid={`season-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{s.nombre}</span>
                      <span className="text-xs text-slate-500">{fmtMMDD(s.date_from)} → {fmtMMDD(s.date_to)}</span>
                      {isAdmin && (
                        <>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-500 hover:text-slate-800"
                            onClick={() => startEditSeason(s)} data-testid={`edit-season-${s.id}`}>
                            <Pencil size={12} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => deleteSeason(s)} data-testid={`delete-season-${s.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-slate-400">+</span>
                      <Input type="number" step="0.5" min="0" value={seasonPcts[s.id] ?? 0}
                        onChange={(e) => setSeasonPcts(prev => ({ ...prev, [s.id]: e.target.value }))}
                        disabled={!isAdmin} className="h-7 w-20" data-testid={`season-pct-${s.id}`} />
                      <span className="text-xs text-slate-400">%</span>
                    </div>
                  </div>
                )
              ))}
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={savePcts} data-testid="save-season-pcts">
                  <Save size={13} className="mr-1" />Guardar %
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Day ranges (global) */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarRange size={18} className="text-slate-600" />
            Rangos de días de alquiler (comunes para todos los modelos)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="day-ranges-list">
            {dayRanges.map(r => (
              <Badge key={rangeKey(r)} variant="outline" className="text-sm py-1.5 px-3 gap-2" data-testid={`range-${rangeKey(r)}`}>
                {rangeLabel(r)}
                {isAdmin && (
                  <button onClick={() => deleteRange(r)} className="text-red-500 hover:text-red-700" data-testid={`delete-range-${rangeKey(r)}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          {isAdmin && (
            <form onSubmit={addRange} className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Desde (días)</Label>
                <Input type="number" min="1" value={newRange.from} onChange={(e) => setNewRange({ ...newRange, from: e.target.value })}
                  className="w-24 h-8" data-testid="new-range-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta (días)</Label>
                <Input type="number" min="1" value={newRange.to} onChange={(e) => setNewRange({ ...newRange, to: e.target.value })}
                  className="w-24 h-8" data-testid="new-range-to" />
              </div>
              <Button type="submit" size="sm" variant="outline" data-testid="add-range-btn">
                <Plus size={14} className="mr-1" />Añadir rango
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Base prices per model per range */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Euro size={18} className="text-green-600" />
            Precios base (Temporada Baja) según los rangos creados (€/día)
          </CardTitle>
          <Select value={priceModelId} onValueChange={setPriceModelId}>
            <SelectTrigger className="w-64 h-9" data-testid="price-model-select"><SelectValue placeholder="Modelo..." /></SelectTrigger>
            <SelectContent>
              {models.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre_modelo}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {dayRanges.length === 0 ? (
            <p className="text-sm text-slate-400">Define primero los rangos de días.</p>
          ) : !priceModel ? (
            <p className="text-sm text-slate-400">Selecciona un modelo.</p>
          ) : (
            <div className="space-y-4" data-testid="base-prices-table">
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                {dayRanges.map(r => (
                  <div key={rangeKey(r)} className="space-y-1">
                    <Label className="text-sm font-semibold text-slate-700">{rangeLabel(r)}</Label>
                    <Input type="number" step="0.01" min="0"
                      value={basePrices[priceModel.id]?.[rangeKey(r)] ?? ''}
                      onChange={(e) => setPrice(priceModel.id, rangeKey(r), e.target.value)}
                      disabled={!isAdmin} placeholder="—"
                      data-testid={`price-${priceModel.id}-${rangeKey(r)}`} />
                  </div>
                ))}
              </div>
              {seasons.length > 0 && (
                <div className="pt-3 border-t border-slate-100 space-y-2">
                  <Label className="text-xs text-slate-500">Temporadas activas para este modelo (desactivada = se mantiene el precio base)</Label>
                  <div className="flex flex-wrap gap-4" data-testid="model-seasons-toggles">
                    {orderedSeasons.map(s => {
                      const disabled = (priceModel.disabled_seasons || []).includes(s.id);
                      return (
                        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`toggle-season-${s.id}`}>
                          <input type="checkbox" checked={!disabled} disabled={!isAdmin}
                            onChange={() => toggleSeasonForModel(priceModel, s.id)}
                            className="w-4 h-4 accent-orange-500" />
                          <span className={disabled ? 'text-slate-400 line-through' : 'text-slate-700'}>
                            {s.nombre} (+{parseFloat(seasonPcts[s.id]) || 0}%)
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="flex justify-end">
                  <Button onClick={() => savePrices(priceModel)} className="bg-green-600 hover:bg-green-700" data-testid={`save-prices-${priceModel.id}`}>
                    <Save size={14} className="mr-1" />Guardar
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Occupancy tiers per model */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp size={18} className="text-blue-600" />
            Incremento del precio final en % según la ocupación — {priceModel ? priceModel.nombre_modelo : 'selecciona un modelo'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-400">
            Se configura por modelo (usa el selector de arriba). Deja todo a 0 si este modelo no debe tener incremento por ocupación.
          </p>
          {priceModel && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="dynamic-tiers">
                {(modelTiers[priceModel.id] || zeroTiers()).map((tr, i) => (
                  <div key={i} className="space-y-1">
                    <Label className="text-xs text-slate-500">{tr.from_pct === 0 ? '0' : tr.from_pct + 1}% - {tr.from_pct + 10}%</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-slate-400 text-sm">+</span>
                      <Input type="number" step="0.5" min="0" value={tr.pct}
                        onChange={(e) => setModelTiers(prev => ({
                          ...prev,
                          [priceModel.id]: (prev[priceModel.id] || zeroTiers()).map((x, j) => j === i ? { ...x, pct: e.target.value } : x),
                        }))}
                        disabled={!isAdmin} className="h-8" data-testid={`tier-${tr.from_pct}`} />
                      <span className="text-slate-400 text-sm">%</span>
                    </div>
                  </div>
                ))}
              </div>
              {isAdmin && (
                <Button onClick={() => saveModelTiers(priceModel)} className="bg-blue-600 hover:bg-blue-700" data-testid="save-tiers-btn">
                  <Save size={15} className="mr-1" />Guardar ocupación de {priceModel.nombre_modelo}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Price preview */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye size={18} className="text-slate-600" />
            Previsualizado de precios
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openExport} data-testid="export-cuadrante-btn">
              <FileSpreadsheet size={14} className="mr-1 text-green-600" />
              Exportar cuadrante (.xlsx)
            </Button>
            <Select value={previewModelId} onValueChange={setPreviewModelId}>
              <SelectTrigger className="w-64 h-9" data-testid="preview-model-select"><SelectValue placeholder="Modelo..." /></SelectTrigger>
              <SelectContent>
                {models.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre_modelo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {!previewModel || dayRanges.length === 0 || seasons.length === 0 ? (
            <p className="text-sm text-slate-400">Selecciona un modelo y configura rangos y temporadas.</p>
          ) : (
            <div className="overflow-x-auto space-y-6" data-testid="price-preview">
              {dayRanges.map(r => {
                const base = parseFloat(basePrices[previewModel.id]?.[rangeKey(r)]);
                return (
                  <table key={rangeKey(r)} className="w-full text-xs whitespace-nowrap">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1.5 pr-3 font-bold text-slate-800">{rangeLabel(r)}</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-slate-600">Precio base</th>
                        {previewTiers.map(tr2 => (
                          <th key={tr2.from_pct} className="text-left py-1.5 px-2 font-semibold text-slate-600">
                            {tr2.from_pct === 0 ? '0' : tr2.from_pct + 1}% - {tr2.from_pct + 10}%
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {orderedSeasons.map(s => {
                        const isDisabled = (previewModel.disabled_seasons || []).includes(s.id);
                        if (isNaN(base)) return (
                          <tr key={s.id} className="border-b border-slate-50">
                            <td className="py-1.5 pr-3 text-slate-600">Temporada {s.nombre.toLowerCase()}</td>
                            <td colSpan={previewTiers.length + 1} className="py-1.5 px-2 text-slate-300">Sin precio base para este rango</td>
                          </tr>
                        );
                        const sBase = base * (1 + (isDisabled ? 0 : (parseFloat(seasonPcts[s.id]) || 0)) / 100);
                        return (
                          <tr key={s.id} className="border-b border-slate-50">
                            <td className="py-1.5 pr-3 text-slate-600">
                              Temporada {s.nombre.toLowerCase()}
                              {isDisabled && <span className="ml-1 text-[10px] text-amber-600">(desactivada · precio base)</span>}
                            </td>
                            <td className="py-1.5 px-2 font-medium text-slate-800">{eur(sBase)}</td>
                            {previewTiers.map(tr2 => (
                              <td key={tr2.from_pct} className="py-1.5 px-2 text-slate-700">
                                {eur(sBase * (1 + (parseFloat(tr2.pct) || 0) / 100))}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export tariff dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar tarifas por modelo (.xlsx)</DialogTitle>
            <DialogDescription>Una pestaña de Excel por modelo, con sus temporadas activas y sus propios precios.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Temporada</Label>
              <Select value={exportSeason} onValueChange={setExportSeason}>
                <SelectTrigger data-testid="export-season-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las temporadas</SelectItem>
                  {orderedSeasons.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre} (+{parseFloat(seasonPcts[s.id]) || 0}%)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Modelos ({exportModels.size} seleccionados)</Label>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2" data-testid="export-models-list">
                {modelsWithPrices.map(m => (
                  <label key={m.id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-slate-50 cursor-pointer">
                    <Checkbox checked={exportModels.has(m.id)}
                      onCheckedChange={() => setExportModels(prev => {
                        const n = new Set(prev);
                        n.has(m.id) ? n.delete(m.id) : n.add(m.id);
                        return n;
                      })}
                      data-testid={`export-model-${m.id}`} />
                    {m.nombre_modelo}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)}>Cancelar</Button>
            <Button onClick={exportCuadrante} className="bg-green-600 hover:bg-green-700" data-testid="confirm-export-btn">
              <FileSpreadsheet size={15} className="mr-1" />Exportar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
